"use client";

import { useEffect, useState } from "react";

/**
 * Holds the old value until the outgoing content has faded, then swaps it.
 *
 * A keyed remount would mount the incoming at zero opacity with nothing where
 * the outgoing was, which is a flash of empty at the wrong moment rather than
 * a handover. Swapping at the invisible point in the middle is what makes the
 * two ends read as one movement.
 *
 * `ms` is the hold before the swap, and it wants to be a little longer than
 * the CSS transition either side of it: matched exactly, the timer can land a
 * frame early and swap at 0.01 opacity, which is a flicker of the old content
 * reshaping. That is the whole reason this is a hold rather than a
 * `transitionend` listener, which does not fire at all when the element is
 * hidden or the transition is interrupted.
 *
 *   const { shown, out } = useCrossfade(phase);
 *   <div className={cn("transition-opacity duration-300", out && "opacity-0")}>
 *     {shown === "done" ? <Done /> : <Form />}
 *   </div>
 */
export function useCrossfade<T>(value: T, ms = 340) {
  const [shown, setShown] = useState(value);
  const [out, setOut] = useState(false);

  useEffect(() => {
    if (Object.is(value, shown)) return;
    setOut(true);
    const timer = setTimeout(() => {
      setShown(value);
      setOut(false);
    }, ms);
    return () => clearTimeout(timer);
  }, [value, shown, ms]);

  return { shown, out };
}
